import LoginRequest from '../../types/LoginRequest';
import LoginResponse from '../../types/LoginResponse';
import MfaResponse from '../../types/MfaResponse';
import Provider from '../../types/Provider';
import SendMFARequest from '../../types/SendMFARequest';
import api from '../api/api';

class AuthService {
  private static loggingOut = false;

  /**
   * Logs in the user (assuming their credentials are valid).
   *
   * This function has the side effect that the returned JWT token representing
   * the user's session is stored in local storage and is used in subsequent API
   * calls until the user logs out.
   *
   * @param email The user's email address.
   * @param password The user's password.
   * @returns The login response with the user's auth information and details.
   */
  static async login(email: string, password: string): Promise<LoginResponse> {
    const request: LoginRequest = {
      email,
      password
    };

    localStorage.removeItem('token');
    AuthService.loggingOut = false;
    const apiResponse = await api.post('/auth/login', request);
    const loginResponse: LoginResponse = apiResponse.data;
    localStorage.setItem('token', loginResponse.token);
    return loginResponse;
  }

  /**
   * Logs in the user (assuming their credentials are valid).
   *
   * This function has the side effect that the returned JWT token representing
   * the user's session is stored in local storage and is used in subsequent API
   * calls until the user logs out.
   *
   * @param provider The provider's name.
   * @param accessToken The provider's access token.
   * @returns The login response with the user's auth information and details.
   */
  static async providerLogin(
    provider: Provider,
    accessToken: string
  ): Promise<LoginResponse> {
    const request = {
      accessToken
    };
    localStorage.removeItem('token');
    AuthService.loggingOut = false;
    const apiResponse = await api.post(`/auth/login/${provider}`, request);
    const loginResponse: LoginResponse = apiResponse.data;
    localStorage.setItem('token', loginResponse.token);
    return loginResponse;
  }

  static async sendEmailVerificationCode(
    signal?: AbortSignal
  ): Promise<string> {
    const config = {
      signal
    };
    const optionalArgs = config ? [config] : [];

    const apiResponse = await api.post('/auth/send-mfa-code', ...optionalArgs);
    return apiResponse.data;
  }

  /**
   * Registers this browser as trusted so future logins can skip the MFA code.
   * The backend sets an HttpOnly trust cookie on the response.
   */
  static async trustDevice(): Promise<void> {
    await api.post('/auth/trust-device');
  }

  static async sendMFACode(
    mfaCode: string,
    trustBrowser: boolean
  ): Promise<MfaResponse> {
    const request: SendMFARequest = {
      mfaCode,
      trustBrowser
    };
    const apiResponse = await api.post('/auth/submit-mfa-code', request);
    const mfaResponse: MfaResponse = apiResponse.data;
    localStorage.setItem('token', mfaResponse.token);
    return mfaResponse;
  }

  /**
   * Retrieves the user's stored JWT token.
   *
   * @returns The user's stored JWT token if present, otherwise `null`.
   */
  static getToken(): string | null {
    return localStorage.getItem('token');
  }

  /**
   * Clears the user's stored JWT token without redirecting.
   * Use this when you want to handle navigation with React Router.
   */
  static clearToken(): void {
    localStorage.removeItem('token');
  }

  /**
   * Calls the logout API endpoint (to record session end) and clears the token.
   * Use this with React Router navigation to avoid the white flash.
   */
  static async logoutAndClearToken(): Promise<void> {
    if (AuthService.loggingOut) return;
    AuthService.loggingOut = true;

    localStorage.removeItem('token');

    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    } finally {
      AuthService.loggingOut = false;
    }
  }

  /**
   * Logs out the user by calling the logout API endpoint (to record session end),
   * removing the user's active session token, and moving to the login page.
   */
  static async logout(): Promise<void> {
    if (AuthService.loggingOut) return;
    AuthService.loggingOut = true;

    localStorage.removeItem('token');

    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    } finally {
      AuthService.loggingOut = false;
    }

    window.location.href = '/login';
  }
}

export default AuthService;
